import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface FitTextOptions {
    minFontSize?: number;
    onFinish?: (fontSize: number) => void;
    onStart?: () => void;
    resolution?: number;
    maxFontSize?: number;
}

interface FitTextState {
    calcKey: number;
    fontSize: number;
    fontSizePrev: number;
    fontSizeMax: number;
    fontSizeMin: number;
}

interface FitTextResult {
    fontSize: string;
    fontSizeValue: number;
    ref: React.RefObject<HTMLDivElement>;
}

const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const getContainerMaxFontSize = (element: HTMLDivElement | null) => {
    if (!element) {
        return 100;
    }

    const style = window.getComputedStyle(element);
    const paddingWidth = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const paddingHeight = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const borderWidth = parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
    const borderHeight = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);

    const availableWidth = element.offsetWidth - paddingWidth - borderWidth;
    const availableHeight = element.offsetHeight - paddingHeight - borderHeight;

    return Math.min(availableWidth, availableHeight) * 100;
};

export const useFitText = ({
    minFontSize = 20,
    maxFontSize,
    onFinish,
    onStart,
    resolution = 5
}: FitTextOptions = {}): FitTextResult => {
    const elementRef = useRef<HTMLDivElement>(null);
    const innerHtmlPrevRef = useRef<string | null>(null);
    const isCalculatingRef = useRef(false);
    const animationFrameRef = useRef<number | null>(null);
    const resizeObserver = useRef<ResizeObserver>();
    const innerHtml = elementRef.current?.innerHTML;

    const getMaxFontSize = useCallback(() => {
        return getContainerMaxFontSize(elementRef.current);
    }, []);

    const initState = useCallback((): FitTextState => {
        const maxSize = maxFontSize !== undefined ? maxFontSize : getMaxFontSize();
        return {
            calcKey: 0,
            fontSize: maxSize,
            fontSizePrev: minFontSize,
            fontSizeMax: maxSize,
            fontSizeMin: minFontSize
        };
    }, [getMaxFontSize, minFontSize, maxFontSize]);

    const [state, setState] = useState<FitTextState>(initState);
    const { calcKey, fontSize, fontSizeMax, fontSizeMin, fontSizePrev } = state;

    // Initialize ResizeObserver
    useEffect(() => {
        resizeObserver.current = new ResizeObserver(() => {
            animationFrameRef.current = window.requestAnimationFrame(() => {
                if (isCalculatingRef.current) {
                    return;
                }

                onStart?.();
                isCalculatingRef.current = true;
                setState((prev) => ({
                    ...initState(),
                    calcKey: prev.calcKey + 1
                }));
            });
        });

        return () => {
            if (animationFrameRef.current) {
                window.cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            resizeObserver.current?.disconnect();
            resizeObserver.current = undefined;
        };
    }, [initState, onStart]);

    // Attach ResizeObserver
    useEffect(() => {
        const currentRef = elementRef.current;
        const observer = resizeObserver.current;

        if (currentRef && observer) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef && observer) {
                observer.unobserve(currentRef);
            }
        };
    }, []);

    useEffect(() => {
        if (calcKey === 0 || isCalculatingRef.current) {
            return;
        }

        if (innerHtml !== innerHtmlPrevRef.current) {
            onStart?.();
            setState((prev) => ({
                ...initState(),
                calcKey: prev.calcKey + 1
            }));
        }
        innerHtmlPrevRef.current = innerHtml ?? null;
    }, [calcKey, initState, onStart, innerHtml]);

    useIsoLayoutEffect(() => {
        if (calcKey === 0) {
            return;
        }

        const isWithinResolution = Math.abs(fontSize - fontSizePrev) <= resolution;
        const currentElement = elementRef.current;
        const isOverflow =
            currentElement &&
            (currentElement.scrollHeight > currentElement.offsetHeight ||
                currentElement.scrollWidth > currentElement.offsetWidth);
        const isFailed = isOverflow && fontSize === fontSizePrev;
        const isAsc = fontSize > fontSizePrev;

        if (isWithinResolution) {
            if (isFailed) {
                isCalculatingRef.current = false;
                onFinish?.(fontSize);
                return;
            }

            if (isOverflow) {
                setState((prev) => ({
                    ...prev,
                    fontSize: isAsc ? fontSizePrev : fontSizeMin
                }));
                return;
            }

            isCalculatingRef.current = false;
            onFinish?.(fontSize);
            return;
        }

        const calculateNewFontSize = () => {
            let delta: number;
            let newMax = fontSizeMax;
            let newMin = fontSizeMin;

            if (isOverflow) {
                delta = isAsc ? fontSizePrev - fontSize : fontSizeMin - fontSize;
                newMax = Math.min(fontSizeMax, fontSize);
            } else {
                delta = isAsc ? fontSizeMax - fontSize : fontSizePrev - fontSize;
                newMin = Math.max(fontSizeMin, fontSize);
            }

            return {
                fontSize: fontSize + delta / 2,
                fontSizeMax: newMax,
                fontSizeMin: newMin
            };
        };

        const { fontSize: newFontSize, fontSizeMax: newMax, fontSizeMin: newMin } = calculateNewFontSize();

        setState((prev) => ({
            ...prev,
            fontSize: newFontSize,
            fontSizeMax: newMax,
            fontSizeMin: newMin,
            fontSizePrev: fontSize
        }));
    }, [calcKey, fontSize, fontSizeMax, fontSizeMin, fontSizePrev, onFinish, resolution, minFontSize]);

    return {
        fontSize: `${fontSize}%`,
        fontSizeValue: fontSize,
        ref: elementRef
    };
};

export default useFitText;
